import { Component, OnInit, OnDestroy, ViewChild, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// PrimeNG imports
import { TableModule, Table } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';

export interface User {
  id: number;
  email: string;
  username: string;
}

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ConfirmDialogModule,
    IconFieldModule,
    InputIconModule,
    SkeletonModule,
    ToastModule
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './users.component.html',
  styleUrl: './users.component.css'
})
export class UsersComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table!: Table;
  
  // Signals
  users = signal<User[]>([]);
  loading = signal(true);
  totalRecords = signal(0);
  
  // Table config
  readonly rows = 10;
  first = 0;
  
  // Display data: empty rows during loading, real data otherwise
  displayData = computed(() => 
    this.loading() ? Array(this.rows).fill({}) : this.users()
  );
  
  private timeout?: number;

  constructor(
    private router: Router,
    private confirmationService: ConfirmationService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadUsers();
  }

  ngOnDestroy(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  private loadUsers(): void {
    if (this.timeout) clearTimeout(this.timeout);
    
    this.loading.set(true);
    this.users.set([]);
    this.totalRecords.set(0);
    
    this.timeout = window.setTimeout(() => {
      const mockUsers = this.generateMockUsers();
      this.users.set(mockUsers);
      this.totalRecords.set(mockUsers.length);
      this.loading.set(false);
    }, 2000);
  }

  private generateMockUsers(): User[] {
    return [
      { id: 1, email: 'john.doe@example.com', username: 'johndoe' },
      { id: 2, email: 'jane.smith@example.com', username: 'janesmith' },
      { id: 3, email: 'mike.johnson@example.com', username: 'mikejohnson' },
      { id: 4, email: 'sarah.wilson@example.com', username: 'sarahwilson' },
      { id: 5, email: 'david.brown@example.com', username: 'davidbrown' },
      { id: 6, email: 'emily.davis@example.com', username: 'emilydavis' },
      { id: 7, email: 'robert.miller@example.com', username: 'robertmiller' },
      { id: 8, email: 'lisa.garcia@example.com', username: 'lisagarcia' },
      { id: 9, email: 'kevin.martinez@example.com', username: 'kevinmartinez' },
      { id: 10, email: 'amanda.taylor@example.com', username: 'amandataylor' },
      { id: 11, email: 'chris.anderson@example.com', username: 'chrisanderson' },
      { id: 12, email: 'michelle.thomas@example.com', username: 'michellethomas' },
      { id: 13, email: 'jason.white@example.com', username: 'jasonwhite' },
      { id: 14, email: 'nicole.harris@example.com', username: 'nicoleharris' },
      { id: 15, email: 'brian.clark@example.com', username: 'brianclark' },
      { id: 16, email: 'stephanie.lewis@example.com', username: 'stephanielewis' },
      { id: 17, email: 'matthew.walker@example.com', username: 'matthewwalker' },
      { id: 18, email: 'jennifer.hall@example.com', username: 'jenniferhall' },
      { id: 19, email: 'daniel.allen@example.com', username: 'danielallen' },
      { id: 20, email: 'kimberly.young@example.com', username: 'kimberlyyoung' }
    ];
  }

  editUser(user: User): void {
    this.router.navigate(['/users', user.id]);
  }

  addUser(): void {
    this.router.navigate(['/users', 1]);
  }

  refreshUsers(): void {
    this.loadUsers();
  }

  deleteUser(user: User): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('users.confirmDelete.message', { username: user.username }),
      header: this.translateService.instant('users.confirmDelete.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      acceptLabel: this.translateService.instant('users.confirmDelete.confirm'),
      rejectLabel: this.translateService.instant('users.confirmDelete.cancel'),
      accept: () => console.log('User deleted:', user)
    });
  }
}